<template>
  <div class="impact-page">
    <impact-header title="影响总览" :tags="['离职 · 淘汰 · 关闭时间', '按受影响学生去重']"
                   :chips="chips" :loaded-at="loadedAt"></impact-header>

    <div class="dash-section">
      <div class="section-head">
        <h2 class="section-title">核心指标</h2>
        <span class="section-legend">
          <i class="legend-dot is-danger"></i>红=预警
          / <i class="legend-dot is-warning"></i>黄=关注
          / <i class="legend-dot is-healthy"></i>绿=健康
        </span>
      </div>
      <div class="section-filters">
        <span class="filter-label">统计区间</span>
        <DatePicker type="daterange" :value="dateRange" format="yyyy-MM-dd" :clearable="false"
                    placement="bottom-start" style="width: 220px" @on-change="onDateChange"></DatePicker>
        <span class="filter-note">※ 环比口径：{{ periodText }} vs {{ prevPeriodText }}（上月同日区间）· 区间最长 93 天</span>
      </div>
      <div class="kpi-grid">
        <Spin v-if="loading" fix></Spin>
        <kpi-card v-for="card in kpiCards" :key="card.key" v-bind="card"></kpi-card>
      </div>
    </div>

    <div class="dash-section">
      <div class="section-head">
        <h2 class="section-title">事件与换老师</h2>
      </div>
      <p class="block-lead">三类事件殊途同归：学生最后都得换老师。看换走了多少人、敞口多大、换完后还剩多少。同一学生被多次波及只归因到最早一起。</p>
      <div class="grid g21">
        <div class="card">
          <div class="card-head">
            <span class="card-title">老师异动事件趋势<span class="card-sub">截至 {{ period.endDate || '—' }} 近 6 个月 · 按生效日 · 按类型拆分</span></span>
            <span class="chart-legend">
              <span v-for="t in typeKeys" :key="t"><i :style="{ background: typeColor(t) }"></i>{{ typeLabel(t) }}</span>
            </span>
          </div>
          <div v-if="!trendCols.length" class="chart-empty">暂无数据</div>
          <div v-else class="cols">
            <div v-for="col in trendCols" :key="col.month" class="col">
              <div class="col-total">{{ col.total }}</div>
              <div class="col-bar" :style="{ height: col.height + 'px' }">
                <div v-for="seg in col.segs" :key="seg.type" :title="seg.title"
                     :style="{ height: seg.height + 'px', background: seg.color }"></div>
              </div>
              <div class="col-label">{{ col.label }}</div>
            </div>
          </div>
          <div class="table-wrap type-table">
            <table class="impact-table">
              <thead>
              <tr>
                <th>异动类型</th>
                <th class="num">事件</th>
                <th class="num">波及学生</th>
                <th class="num">7 天换完率</th>
                <th class="num">平均通知天数</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="row in byType" :key="row.type">
                <td><span class="badge" :class="typeBadge(row.type)">{{ typeLabel(row.type) }}</span></td>
                <td class="num">{{ fmtNum(row.events) }}</td>
                <td class="num">{{ fmtNum(row.students) }}</td>
                <td class="num">{{ fmtRate(row.switched7dRate) }}</td>
                <td class="num">{{ fmtNum(row.avgNoticeDays) }}{{ isNum(row.avgNoticeDays) ? ' 天' : '' }}</td>
              </tr>
              <tr v-if="!byType.length"><td colspan="5" class="empty">暂无数据</td></tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="card">
          <div class="card-head">
            <span class="card-title">换老师漏斗<span class="card-sub">{{ fmtNum(d.eventCount) }} 起异动 → {{ fmtNum(funnel.students) }} 名学生</span></span>
          </div>
          <div class="summary-strip">
            <span>未消课时敞口 <b>{{ fmtNum(d.exposureHours) }}</b> 课时</span>
            <span>超 7 天待换 <b class="is-danger">{{ fmtNum(funnel.waitingOver7) }} 人</b></span>
            <span>换老师且改时 <b class="is-danger">{{ fmtNum(funnel.changeSlot) }} 人</b></span>
          </div>
          <div v-for="row in funnelRows" :key="row.key" class="funnel-row">
            <div class="funnel-label">{{ row.label }}</div>
            <div class="funnel-track">
              <div class="funnel-fill" :style="{ width: row.width + '%', background: row.color }">{{ fmtNum(row.value) }}</div>
            </div>
            <div class="funnel-rate">{{ row.rate }}</div>
          </div>
          <div class="note is-warning" v-if="noticeInsight">{{ noticeInsight }}</div>
        </div>
      </div>
    </div>

    <div class="dash-section">
      <div class="section-head">
        <h2 class="section-title">影响程度与风险池</h2>
      </div>
      <p class="block-lead">损失课次看伤害深度，被波及次数看重复伤害，风险池看本周要处理的事件。</p>
      <div class="grid g3">
        <div class="card">
          <div class="card-head">
            <span class="card-title">影响程度分布<span class="card-sub">受影响学生 · 损失课次</span></span>
          </div>
          <bar-list :rows="depthRows" unit="人"></bar-list>
          <div class="note">损失课次 = 生效后 14 天应上（原周课量 × 2）− 实上，不足 14 天按已过整周折算。</div>
        </div>
        <div class="card">
          <div class="card-head">
            <span class="card-title">影响频率分布<span class="card-sub">{{ period.endDate || '—' }} 往前 90 天 · 被波及次数</span></span>
          </div>
          <bar-list :rows="freqRows" unit="人"></bar-list>
          <div class="note is-warning">
            <b>{{ fmtRate(d.repeatRate) }} 的学生在 90 天内被波及 2 次及以上</b>，这批人贡献了 {{ fmtRate(d.repeatLostShare) }} 的损失课次，是最优先人工干预的群体。
          </div>
        </div>
        <div class="card">
          <div class="card-head">
            <span class="card-title">待处理风险池<span class="card-sub">生效日在今天前 7 天 ~ 后 14 天</span></span>
          </div>
          <div class="table-wrap">
            <table class="impact-table">
              <thead>
              <tr>
                <th>老师</th>
                <th>类型</th>
                <th class="num">波及学生</th>
                <th class="num">未消课时</th>
                <th>生效日</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="row in riskPool" :key="row.type + '-' + row.tchId + '-' + row.effectiveDate">
                <td class="t-name">{{ row.tchName || row.tchId }}</td>
                <td><span class="badge" :class="typeBadge(row.type)">{{ typeLabel(row.type) }}</span></td>
                <td class="num">{{ fmtNum(row.students) }}</td>
                <td class="num">{{ fmtNum(row.exposureHours) }}</td>
                <td>
                  {{ shortDate(row.effectiveDate) }}
                  <span v-if="row.effectiveDate > todayStr" class="t-muted">未生效</span>
                </td>
              </tr>
              <tr v-if="!riskPool.length"><td colspan="5" class="empty">暂无待处理事件</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import ImpactHeader from './parts/ImpactHeader.vue'
import KpiCard from './parts/KpiCard.vue'
import BarList from './parts/BarList.vue'
import {
  postImpact, num, isNum, fmtNum, fmtRate, fmtRateNum, shortDate, typeLabel, typeColor, typeBadge,
  levelHigh, levelLow, levelVsPrev, changeVsPrev
} from './parts/impact'

const DEPTH_COLORS = ['#0B9E8D', '#8FA3C7', '#E89F2D', '#E6464E', '#B3312F']
const FREQ_COLORS = ['#C9D3E8', '#E89F2D', '#E6464E', '#B3312F']
const MAX_DAYS = 93

export default {
  name: 'TchImpactOverview',
  components: { ImpactHeader, KpiCard, BarList },
  data () {
    let today = this.$moment()
    return {
      loading: false,
      loadedAt: '',
      todayStr: today.format('YYYY-MM-DD'),
      dateRange: [today.clone().startOf('month').format('YYYY-MM-DD'), today.format('YYYY-MM-DD')],
      typeKeys: ['resign', 'close', 'elim'],
      d: {}
    }
  },
  computed: {
    period () {
      return this.d.period || {}
    },
    periodText () {
      let p = this.period
      return p.startDate ? p.startDate + ' ~ ' + p.endDate : (this.dateRange[0] + ' ~ ' + this.dateRange[1])
    },
    prevPeriodText () {
      let p = this.period
      return p.prevStartDate ? p.prevStartDate + ' ~ ' + p.prevEndDate : '上期'
    },
    funnel () {
      return this.d.funnel || {}
    },
    byType () {
      return this.d.byType || []
    },
    riskPool () {
      return this.d.riskPool || []
    },
    chips () {
      return [
        { label: '统计区间', value: this.periodText },
        { label: '对比期', value: this.prevPeriodText },
        { label: '老师范围', value: '欧美外教' },
        { label: '事件', value: '离职 / 淘汰 / 关闭时间' }
      ]
    },
    switched7dRate () {
      let f = this.funnel
      return num(f.students) > 0 ? Math.round(num(f.switched7d) / num(f.students) * 1000) / 10 : null
    },
    kpiCards () {
      let d = this.d
      let f = this.funnel
      let ev = changeVsPrev(d.eventCount, d.prevEventCount, ' 起')
      let st = changeVsPrev(d.studentCount, d.prevStudentCount, ' 人')
      let retainGap = isNum(d.retain30Rate) && isNum(d.controlRetain30Rate)
        ? Math.round((Number(d.retain30Rate) - Number(d.controlRetain30Rate)) * 10) / 10 : null
      let medianDiff = isNum(d.switchMedianDays) && isNum(d.prevSwitchMedianDays)
        ? Math.round((Number(d.switchMedianDays) - Number(d.prevSwitchMedianDays)) * 10) / 10 : null
      return [
        {
          key: 'event',
          title: '老师异动事件',
          value: fmtNum(d.eventCount),
          unit: '起',
          level: levelVsPrev(d.eventCount, d.prevEventCount),
          period: this.periodText,
          compare: ev.text,
          compareTone: ev.tone,
          foot: '离职 + 淘汰 + 关闭时间，按生效日计数；同一老师多次关闭时间算多起'
        },
        {
          key: 'student',
          title: '波及学生',
          value: fmtNum(d.studentCount),
          unit: '人',
          level: levelLow(d.studentRate, 2, 5),
          period: '占活跃学生 ' + fmtRate(d.studentRate),
          compare: st.text,
          compareTone: st.tone,
          foot: '活跃 = 区间内有完成常规外教课，共 ' + fmtNum(d.activeStudentCount) + ' 人'
        },
        {
          key: 'exposure',
          title: '未消课时敞口',
          value: fmtNum(d.exposureHours),
          unit: '课时',
          level: num(d.exposureHours) > 0 ? 'warning' : 'healthy',
          period: '人均 ' + fmtNum(d.avgExposureHours) + ' 课时',
          compare: '',
          foot: '波及学生当前剩余课时之和（非事件时点快照）'
        },
        {
          key: 'median',
          title: '换老师时长中位数',
          value: fmtNum(d.switchMedianDays),
          unit: '天',
          level: levelLow(d.switchMedianDays, 4, 8),
          period: 'P90 ' + fmtNum(d.switchP90Days) + (isNum(d.switchP90Days) ? ' 天' : ''),
          compare: medianDiff == null ? '' : (medianDiff === 0 ? '与上期持平' : (medianDiff > 0 ? '↑ ' : '↓ ') + Math.abs(medianDiff) + ' 天 vs 上期'),
          compareTone: medianDiff == null || medianDiff === 0 ? 'neutral' : (medianDiff > 0 ? 'danger' : 'healthy'),
          foot: '生效日 → 学生首次已选新老师常规外教课，已换老师学生'
        },
        {
          key: 'retain',
          title: '异动后 30 天留存',
          value: fmtRateNum(d.retain30Rate),
          unit: '%',
          level: retainGap == null ? 'healthy' : (retainGap >= -2 ? 'healthy' : (retainGap >= -5 ? 'warning' : 'danger')),
          period: '对照组 ' + fmtRate(d.controlRetain30Rate),
          compare: retainGap == null ? '' : '差值 ' + (retainGap > 0 ? '+' : '') + retainGap + 'pp',
          compareTone: retainGap == null ? 'neutral' : (retainGap < 0 ? 'danger' : 'healthy'),
          foot: '生效满 30 天的学生才评估，共 ' + fmtNum(d.retainEligibleCount) + ' 人；30 天内有完成课即留存'
        },
        {
          key: 'switch7',
          title: '7 天换完率',
          value: fmtRateNum(this.switched7dRate),
          unit: '%',
          level: levelHigh(this.switched7dRate, 80, 60),
          period: '7 天换完 ' + fmtNum(f.switched7d) + ' / 波及 ' + fmtNum(f.students),
          compare: '',
          foot: '生效后 7 天内已选新老师常规外教课'
        },
        {
          key: 'wait7',
          title: '超 7 天待换老师',
          value: fmtNum(f.waitingOver7),
          unit: '人',
          level: num(f.waitingOver7) > 0 ? 'danger' : 'healthy',
          period: '已换老师 ' + fmtNum(f.switched) + ' 人',
          compare: '',
          foot: '生效已过 7 天仍未在新老师处上课，最该人工介入'
        },
        {
          key: 'repeat',
          title: '重复受影响率',
          value: fmtRateNum(d.repeatRate),
          unit: '%',
          level: levelLow(d.repeatRate, 5, 10),
          period: '90 天内被波及 ≥ 2 次',
          compare: isNum(d.repeatLostShare) ? '贡献损失 ' + fmtRate(d.repeatLostShare) : '',
          compareTone: 'danger',
          foot: '异动 + 请假/旷课课节都算一次波及'
        }
      ]
    },
    trendCols () {
      let list = this.d.trend || []
      let keys = this.typeKeys
      let max = 1
      list.forEach(function (m) {
        let t = num(m.resign) + num(m.close) + num(m.elim)
        if (t > max) {
          max = t
        }
      })
      return list.map(function (m) {
        let total = num(m.resign) + num(m.close) + num(m.elim)
        let label = m.month && m.month.length >= 7 ? Number(m.month.slice(5, 7)) + '月' : (m.month || '')
        return {
          month: m.month,
          label: label,
          total: total,
          height: Math.round(total / max * 150),
          segs: keys.slice().reverse().map(function (t) {
            return {
              type: t,
              color: typeColor(t),
              title: typeLabel(t) + ' ' + num(m[t]),
              height: Math.round(num(m[t]) / max * 150)
            }
          })
        }
      })
    },
    funnelRows () {
      let f = this.funnel
      let total = num(f.students)
      let base = total || 1
      let pct = function (v) {
        return total > 0 ? fmtRate(num(v) / total * 100) : '—'
      }
      return [
        { key: 'students', label: '波及学生', value: f.students, rate: total > 0 ? '100%' : '—', color: '#8FA3C7' },
        { key: 'switched', label: '已完成换老师', value: f.switched, rate: pct(f.switched), color: '#5C78A8' },
        { key: 'switched7d', label: '7 天内换完', value: f.switched7d, rate: pct(f.switched7d), color: '#2A3C62' },
        { key: 'retained30', label: '30 天留存', value: f.retained30, rate: fmtRate(this.d.retain30Rate), color: '#0B9E8D' }
      ].map(function (row) {
        return Object.assign(row, { width: Math.max(num(row.value) / base * 100, num(row.value) > 0 ? 10 : 0) })
      })
    },
    noticeInsight () {
      let list = this.byType.filter(function (r) {
        return num(r.students) > 0
      })
      if (!list.length) {
        return ''
      }
      let parts = list.map(function (r) {
        return typeLabel(r.type) + '平均通知 ' + fmtNum(r.avgNoticeDays) + ' 天、7 天换完 ' + fmtRate(r.switched7dRate)
      })
      return '按类型拆：' + parts.join('；') + '。通知窗口越短，7 天换完率通常越低——先看是不是通知太晚，再看老师资源。'
    },
    depthRows () {
      return (this.d.depth || []).map(function (r, i) {
        return { key: r.label, label: r.label, value: r.count, color: DEPTH_COLORS[i] || DEPTH_COLORS[4] }
      })
    },
    freqRows () {
      return (this.d.freq || []).map(function (r, i) {
        return { key: r.label, label: r.label, value: r.count, color: FREQ_COLORS[i] || FREQ_COLORS[3] }
      })
    }
  },
  mounted () {
    this.load()
  },
  methods: {
    num: num,
    isNum: isNum,
    fmtNum: fmtNum,
    fmtRate: fmtRate,
    shortDate: shortDate,
    typeLabel: typeLabel,
    typeColor: typeColor,
    typeBadge: typeBadge,
    onDateChange (val) {
      if (!val || !val[0] || !val[1]) {
        return
      }
      let days = this.$moment(val[1]).diff(this.$moment(val[0]), 'days') + 1
      if (days > MAX_DAYS) {
        this.$Message.error('统计区间不能超过 93 天')
        this.dateRange = this.dateRange.slice()
        return
      }
      this.dateRange = [val[0], val[1]]
      this.load()
    },
    load () {
      let self = this
      self.loading = true
      postImpact(self, '/overview', { startDate: self.dateRange[0], endDate: self.dateRange[1] }).then(function (data) {
        if (data) {
          self.d = data
          self.loadedAt = self.$moment().format('YYYY-MM-DD HH:mm:ss')
        }
      }).finally(function () {
        self.loading = false
      })
    }
  }
}
</script>
<style scoped lang="less">
@import './parts/page.less';

.filter-label {
  font-size: 12px;
  color: @text-muted;
}

.cols {
  display: flex;
  align-items: flex-end;
  gap: 18px;
  height: 190px;
  padding: 6px 4px 0;
}

.col {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  height: 100%;
}

.col-total {
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 600;
  color: @text-title;
}

.col-bar {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  width: 100%;
  max-width: 46px;
  border-radius: 3px 3px 0 0;
  overflow: hidden;
  div {
    width: 100%;
  }
}

.col-label {
  margin-top: 6px;
  font-size: 12px;
  color: @text-muted;
}

.type-table {
  margin-top: 16px;
}
</style>



// WEBPACK FOOTER //
// TchImpactOverview.vue?3c88a052